import React, { useState } from 'react';
import { Modal, Form, Select, Input, message } from 'antd';
import {
  ClockCircleOutlined,
} from '@ant-design/icons';
import { createReportSchedule, updateReportSchedule } from '../../services/regulatoryReportingService';

const { Option } = Select;
const { TextArea } = Input;

const REPORT_TYPES = [
  { value: 'CBN_RETURNS', label: 'CBN Returns' },
  { value: 'NDIC_RETURNS', label: 'NDIC Returns' },
  { value: 'AML_CTR', label: 'AML — Currency Transaction Report (CTR)' },
  { value: 'AML_SAR', label: 'AML — Suspicious Activity Report (SAR)' },
  { value: 'AML_STR', label: 'AML — Suspicious Transaction Report (STR)' },
  { value: 'NFIU_REPORT', label: 'NFIU Report' },
  { value: 'EFCC_REPORT', label: 'EFCC Compliance Report' },
  { value: 'CREDIT_BUREAU', label: 'Credit Bureau Report' },
  { value: 'CAPITAL_ADEQUACY', label: 'Capital Adequacy Report' },
  { value: 'LIQUIDITY_REPORT', label: 'Liquidity Report' },
  { value: 'PRUDENTIAL_RETURNS', label: 'Prudential Returns' },
  { value: 'RISK_ASSESSMENT', label: 'Risk Assessment Report' },
  { value: 'KYC_COMPLIANCE', label: 'KYC Compliance Report' },
  { value: 'TRANSACTION_MONITORING', label: 'Transaction Monitoring Report' },
  { value: 'CUSTOM', label: 'Custom Report' },
];

const FREQUENCIES = [
  { value: 'DAILY', label: 'Daily' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
];

const ScheduleFormModal = ({ visible, onClose, onSuccess, editRecord = null }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const isEditing = !!editRecord;

  // Pre-fill form when editing
  React.useEffect(() => {
    if (editRecord && visible) {
      form.setFieldsValue({
        reportType: editRecord.reportType,
        frequency: editRecord.frequency,
        description: editRecord.description,
      });
    } else if (!visible) {
      form.resetFields();
    }
  }, [editRecord, visible, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const payload = {
        reportType: values.reportType,
        frequency: values.frequency,
        description: values.description || '',
      };

      let res;
      if (isEditing) {
        res = await updateReportSchedule(editRecord.id, payload);
      } else {
        res = await createReportSchedule(payload);
      }

      if (res?.status) {
        message.success(`Schedule ${isEditing ? 'updated' : 'created'} successfully`);
        form.resetFields();
        onSuccess?.();
        onClose();
      } else {
        message.error(res?.message || `Failed to ${isEditing ? 'update' : 'create'} schedule`);
      }
    } catch (err) {
      if (err?.errorFields) return;
      console.error('Schedule error:', err);
      message.error('An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#fff' }}>
          <ClockCircleOutlined style={{ color: '#ff6700' }} />
          {isEditing ? 'Edit Schedule' : 'Create Report Schedule'}
        </span>
      }
      open={visible}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={isEditing ? 'Update Schedule' : 'Create Schedule'}
      okButtonProps={{
        loading,
        style: {
          background: '#ff6700',
          borderColor: '#ff6700',
          borderRadius: 8,
          fontWeight: 700,
        },
      }}
      cancelButtonProps={{ style: { borderRadius: 8 } }}
      width={560}
      className="reg-form-modal"
      centered
      destroyOnClose
    >
      <Form form={form} layout="vertical" requiredMark="optional">
        <Form.Item
          name="reportType"
          label="Report Type"
          rules={[{ required: true, message: 'Please select a report type' }]}
        >
          <Select
            placeholder="Select report type"
            size="large"
            showSearch
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
          >
            {REPORT_TYPES.map((rt) => (
              <Option key={rt.value} value={rt.value}>
                {rt.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="frequency"
          label="Frequency"
          rules={[{ required: true, message: 'Please select a frequency' }]}
        >
          <Select placeholder="Select frequency" size="large">
            {FREQUENCIES.map((f) => (
              <Option key={f.value} value={f.value}>
                {f.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="description"
          label="Description (Optional)"
        >
          <TextArea
            rows={3}
            placeholder="Add any notes for this schedule..."
            maxLength={300}
            showCount
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ScheduleFormModal;
